import { apiCall, apiCallWithMeta } from './api';
import type { User, UserRole, MyGameSummary } from '@sulis-liiga/common';
import { API_ROUTES } from '@sulis-liiga/common';

// Interface for profile update
interface UpdateProfileData {
  firstName?: string;
  lastName?: string;
  email?: string;
  profilePicture?: string;
  phone?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
  };
  preferences?: {
    emailOnPendingAction?: boolean;
    autoApproveResults?: boolean;
  };
}

const userService = {
  /**
   * Get the current user profile
   */
  getCurrentUserProfile: async (): Promise<User> => {
    return apiCall<User>('get', API_ROUTES.USER.PROFILE);
  },

  /**
   * Update the current user profile
   */
  updateProfile: async (profileData: UpdateProfileData): Promise<User> => {
    return apiCall<User>('put', API_ROUTES.USER.UPDATE_PROFILE, profileData);
  },

  /**
   * Change the current user password
   */
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    return apiCall<void>('post', API_ROUTES.USER.CHANGE_PASSWORD, {
      currentPassword,
      newPassword,
    });
  },

  /**
   * Get a list of users (admin only)
   */
  getAllUsers: async (filters?: {
    role?: UserRole;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    users: User[];
    total: number;
    page: number;
    limit: number;
  }> => {
    let url = API_ROUTES.USER.LIST;

    // Add query parameters for filters. The backend reads the free-text term as
    // `q` (not `search`) and returns the standard { data, meta } envelope.
    if (filters) {
      const params = new URLSearchParams();
      if (filters.role) params.append('role', filters.role);
      if (filters.search) params.append('q', filters.search);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      if (params.toString()) {
        url = `${url}?${params.toString()}`;
      }
    }

    // The list endpoint returns { data: User[], meta: { total, page, limit } } —
    // use apiCallWithMeta so we keep the pagination metadata.
    const { data, meta } = await apiCallWithMeta<User[]>('get', url);
    return {
      users: data ?? [],
      total: meta?.total ?? 0,
      page: meta?.page ?? filters?.page ?? 1,
      limit: meta?.limit ?? filters?.limit ?? data?.length ?? 0,
    };
  },

  /**
   * Get a specific user by ID (admin only)
   */
  getUserById: async (userId: string): Promise<User> => {
    return apiCall<User>('get', API_ROUTES.USER.DETAILS(userId));
  },

  /**
   * Update a specific user (admin only)
   */
  updateUser: async (userId: string, userData: UpdateProfileData): Promise<User> => {
    return apiCall<User>('put', API_ROUTES.USER.DETAILS(userId), userData);
  },

  /**
   * Update user roles (admin only)
   */
  updateUserRoles: async (userId: string, roles: UserRole[]): Promise<User> => {
    return apiCall<User>('put', `${API_ROUTES.USER.DETAILS(userId)}/roles`, { roles });
  },

  /**
   * Get user statistics
   */
  getUserStatistics: async (userId: string): Promise<Record<string, unknown>> => {
    return apiCall<Record<string, unknown>>('get', `${API_ROUTES.USER.DETAILS(userId)}/statistics`);
  },

  /**
   * Get user games
   */
  getUserGames: async (userId: string): Promise<Record<string, unknown>[]> => {
    return apiCall<Record<string, unknown>[]>('get', `${API_ROUTES.USER.DETAILS(userId)}/games`);
  },

  /**
   * Get user teams
   */
  getUserTeams: async (userId: string): Promise<Record<string, unknown>[]> => {
    return apiCall<Record<string, unknown>[]>('get', `${API_ROUTES.USER.DETAILS(userId)}/teams`);
  },

  /**
   * Fetch the authenticated player's upcoming games across ALL tenants.
   * Calls the cross-tenant `GET /v1/users/me/games` endpoint which is
   * exempt from tenant-prefixing (covered by the `/v1/users` global prefix).
   */
  getMyUpcomingGames: async (status?: string): Promise<MyGameSummary[]> => {
    const url = status
      ? `/v1/users/me/games?status=${encodeURIComponent(status)}`
      : '/v1/users/me/games';
    return apiCall<MyGameSummary[]>('get', url);
  },

  /**
   * Find a user by exact email match using the search endpoint (case-insensitive).
   * Returns the user or null if no user with that email exists.
   */
  findUserByEmail: async (email: string): Promise<User | null> => {
    // The backend search endpoint uses the `q` query param (req.query.q)
    // and returns ApiResponse<UserDto[]> which apiCall unwraps to UserDto[]
    const url = `${API_ROUTES.USER.LIST}?q=${encodeURIComponent(email)}`;
    const users = await apiCall<User[]>('get', url);
    if (!Array.isArray(users)) {
      return null;
    }
    const match = users.find(u => u.email.toLowerCase() === email.toLowerCase());
    return match ?? null;
  },
};

export default userService;
